/**
 * La tabla de campañas de un comercio: cada una con cuándo salió y cómo le fue, y el link a su detalle. En el celular
 * scrollea de costado (la tabla entera, no la página).
 *
 * «Mails» cuenta cada mail (una secuencia de 3 pasos son hasta 3 por persona); las demás columnas son personas, con su
 * tasa sobre las personas.
 */

import { Link, useNavigate } from "react-router-dom";
import type { CampanaFila } from "../types";
import { num } from "../../campana-nueva/utils/format";
import { R_EMAILS, tasaTexto, textoSalida } from "../logic/emails";
import { ChipEstadoCampana } from "./Chips";

function Celda({ n, t, sinMedir }: { n: number | null | undefined; t: number | null | undefined; sinMedir?: boolean }) {
  if (sinMedir) return <span className="text-xs text-slate-400">sin medir</span>;
  return (
    <span className="whitespace-nowrap">
      <span className="font-semibold text-slate-900 tabular-nums">{tasaTexto(t)}</span>
      <span className="ml-1 text-xs text-slate-400 tabular-nums">{n === null || n === undefined ? "" : num(n)}</span>
    </span>
  );
}

export function TablaCampanas({ filas }: { filas: CampanaFila[] }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Campaña</th>
              <th className="px-4 py-2.5">Salió</th>
              <th className="px-4 py-2.5 text-right">Mails</th>
              <th className="px-4 py-2.5">Les llegó</th>
              <th className="px-4 py-2.5">Abrieron</th>
              <th className="px-4 py-2.5">Clic</th>
              <th className="px-4 py-2.5">Respondieron</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filas.map((c) => {
              const ruta = `${R_EMAILS}/campanas/${encodeURIComponent(String(c.id))}`;
              const k = c.conteos;
              return (
                <tr key={c.id} onClick={() => navigate(ruta)} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={ruta} onClick={(e) => e.stopPropagation()} className="font-semibold text-slate-900 hover:underline">{c.titulo || `Campaña ${c.id}`}</Link>
                      <ChipEstadoCampana estado={c.estado} faltan={c.progreso?.faltan} />
                    </div>
                    {c.remitentes[0] && <p className="mt-0.5 text-xs text-slate-400">desde {c.remitentes.map((r) => r.email).join(", ")}</p>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{textoSalida(c.primerEnvio, c.ultimoEnvio)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">{k ? num(k.enviados) : "—"}</td>
                  <td className="px-4 py-3"><Celda n={k?.entregados} t={c.tasas?.entrega} /></td>
                  <td className="px-4 py-3"><Celda n={k?.abrieron} t={c.tasas?.apertura} sinMedir={!!k && k.abrieron === null} /></td>
                  <td className="px-4 py-3"><Celda n={k?.hicieronClic} t={c.tasas?.clic} sinMedir={!!k && k.hicieronClic === null} /></td>
                  <td className="px-4 py-3"><Celda n={k?.respondieron} t={c.tasas?.respuesta} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">«Mails» cuenta cada mail que salió (cada paso de una secuencia); lo demás son personas, con su porcentaje.</p>
    </div>
  );
}

export default TablaCampanas;
